import { Button, Dropdown } from "@heroui/react";
import { useMemo } from "react";

import { GlobeIcon } from "@/src/components/icons";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import { isLocale, LOCALES } from "@/src/i18n/translate";

/**
 * Choix de la langue — le voisin de `ThemeToggle`.
 *
 * Un menu plutôt qu'une bascule à deux états : une bascule montrerait soit la
 * langue courante, soit celle vers laquelle elle mène, et rien à l'écran ne
 * dirait laquelle des deux. Ici la coche répond à la question.
 *
 * Le déclencheur affiche le code de la langue **à côté** du globe : le globe
 * seul dit « il y a un réglage de langue », pas « tu es en anglais ». C'est la
 * différence avec le thème, qui se voit sans qu'on l'annonce.
 */
export function LocaleToggle({ className }: { className?: string }) {
  const { locale, setLocale } = useLocale();
  const ta = useTranslations("a11y");
  const tl = useTranslations("locales");

  // Mémoïsé pour ne pas repasser un tableau neuf à chaque render (react-perf).
  const selectedKeys = useMemo(() => [locale], [locale]);

  return (
    <Dropdown>
      {/*
        Le `Button` de HeroUI, pas `Dropdown.Trigger` : ce dernier est un bouton
        React Aria nu, qui n'a ni la taille ni la variante du reste de la barre.
        Les deux fonctionnent comme déclencheur — `MenuTrigger` câble n'importe
        quel bouton React Aria, et celui de HeroUI en est un.
      */}
      <Button
        aria-label={ta("change-language")}
        className={className}
        size="sm"
        variant="ghost"
      >
        <GlobeIcon className="size-4" />
        <span className="text-xs font-medium uppercase">{locale}</span>
      </Button>

      <Dropdown.Popover placement="bottom end">
        <Dropdown.Menu
          // Sans ça, re-cliquer la langue courante la désélectionne et l'admin
          // se retrouve sans langue choisie.
          disallowEmptySelection
          selectedKeys={selectedKeys}
          selectionMode="single"
          onSelectionChange={(keys) => {
            // `Selection` vaut « all » ou un ensemble de clés ; en sélection
            // simple seul le second cas se produit, mais le type couvre les deux.
            const [selected] = keys === "all" ? [] : [...keys];
            // Une clé React Aria peut être un nombre : on ne retient que les
            // chaînes, et `isLocale` rejette le reste.
            const value = typeof selected === "string" ? selected : null;
            if (isLocale(value)) setLocale(value);
          }}
        >
          {LOCALES.map((value) => (
            <Dropdown.Item id={value} key={value} textValue={tl(value)}>
              {tl(value)}
              <Dropdown.ItemIndicator />
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown>
  );
}
